/** Thin, pure-ish HTTP client: every call returns a Result; the only state is the session token in localStorage. */
export type ApiError = {
  readonly code: string;
  readonly message: string;
  readonly status: number;
  readonly details?: unknown;
};
export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ApiError };

const TOKEN_KEY = 'autodm.session';
const UNLOCK_KEY = 'autodm.unlock';

export const session = {
  get: (): string | null => localStorage.getItem(TOKEN_KEY),
  set: (token: string): void => localStorage.setItem(TOKEN_KEY, token),
  clear: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(UNLOCK_KEY);
  },
  unlock: {
    get: (): string | null => localStorage.getItem(UNLOCK_KEY),
    set: (t: string): void => localStorage.setItem(UNLOCK_KEY, t),
  },
};

type Listener = (e: ApiError) => void;
const listeners = new Set<Listener>();
/** Subscribe to 401s so the shell can redirect to login without threading callbacks everywhere. */
export const onUnauthorized = (l: Listener): (() => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

const toApiError = (status: number, json: unknown): ApiError => {
  const e = (json as { error?: Partial<ApiError> } | null)?.error ?? {};
  return {
    code: e.code ?? 'http_error',
    message: e.message ?? `request failed (${status})`,
    status,
    details: e.details,
  };
};

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<ApiResult<T>> {
  const token = session.get();
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...extraHeaders,
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...(body === undefined ? {} : { 'content-type': 'application/json' }),
  };
  try {
    const res = await fetch(path, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : null;
    if (res.ok) return { ok: true, value: json as T };
    const error = toApiError(res.status, json);
    if (res.status === 401 && token) for (const l of listeners) l(error);
    return { ok: false, error };
  } catch (e) {
    return {
      ok: false,
      error: {
        code: 'network_error',
        message: e instanceof Error ? e.message : 'network failure',
        status: 0,
      },
    };
  }
}

export const api = {
  get: <T>(path: string, headers?: Record<string, string>) =>
    request<T>('GET', path, undefined, headers),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('POST', path, body ?? {}, headers),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

/** Unwrap for React Query: throw the ApiError so `isError`/`error` carry it. */
export async function unwrap<T>(p: Promise<ApiResult<T>>): Promise<T> {
  const r = await p;
  if (!r.ok) throw r.error;
  return r.value;
}

export const qs = (
  params: Record<string, string | number | boolean | undefined | null>,
): string => {
  const entries = Object.entries(params).filter(
    (kv): kv is [string, string | number | boolean] =>
      kv[1] !== undefined && kv[1] !== null && kv[1] !== '',
  );
  return entries.length
    ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`
    : '';
};
